import { Client } from '@colyseus/sdk';
import { ROOM_NAME, type JoinOptions } from '@orbomb/protocol';

export interface MatchSocket {
  send(type: string, payload: unknown): void;
  onMessage(type: string, handler: (payload: unknown) => void): void;
  onDrop?(handler: () => void): void;
  onReconnect?(handler: () => void): void;
  onLeave?(handler: () => void): void;
  disableReconnection?(): void;
  leave(consented?: boolean): void;
  reconnectionToken?: string;
}

export type ConnectMatch = (options: JoinOptions) => Promise<MatchSocket>;
export type ReconnectMatch = (reconnectionToken: string) => Promise<MatchSocket>;

/** Keep total retry budget under the server seat hold (`MATCH_RECONNECT_SECONDS` = 10). */
export const COLYSEUS_RECONNECT = {
  enabled: true,
  maxRetries: 5,
  delay: 200,
  minDelay: 200,
  maxDelay: 1800,
} as const;

export function configureReconnection(target: {
  enabled: boolean;
  maxRetries: number;
  delay: number;
  minDelay: number;
  maxDelay: number;
}): void {
  target.enabled = COLYSEUS_RECONNECT.enabled;
  target.maxRetries = COLYSEUS_RECONNECT.maxRetries;
  target.delay = COLYSEUS_RECONNECT.delay;
  target.minDelay = COLYSEUS_RECONNECT.minDelay;
  target.maxDelay = COLYSEUS_RECONNECT.maxDelay;
}

export function stopReconnection(target: { enabled: boolean }): void {
  target.enabled = false;
}

/** Build-time `VITE_COLYSEUS_URL`, trimmed. Empty when the bundle was built without it. */
export function configuredColyseusUrl(): string {
  const env = (import.meta as { env?: Record<string, unknown> }).env?.VITE_COLYSEUS_URL;
  return typeof env === 'string' ? env.trim().replace(/\/$/, '') : '';
}

/**
 * `VITE_COLYSEUS_URL` wins. Without it the client guesses port 2567 on the page
 * host, which only holds for `pnpm dev:online`; a hosted https page ends up
 * calling `https://<site>:2567` where nothing answers.
 */
export function resolveColyseusUrl(
  configured: string,
  location: { protocol: string; hostname: string } | null,
): string {
  const url = configured.trim().replace(/\/$/, '');
  if (url) return url;
  if (!location) return 'http://localhost:2567';
  const protocol = location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${location.hostname}:2567`;
}

export function readColyseusUrl(): string {
  return resolveColyseusUrl(
    configuredColyseusUrl(),
    typeof window === 'undefined' ? null : window.location,
  );
}

/**
 * Browsers report every network-level failure of the matchmaking `fetch` with
 * one opaque message: WebKit "Load failed", Chromium "Failed to fetch",
 * Firefox "NetworkError when attempting to fetch resource.". None of them
 * names the server, so the HUD line was useless on a phone.
 */
const NETWORK_FAILURE =
  /load failed|failed to fetch|networkerror|network error|network request failed|fetch failed|econnrefused|enotfound|etimedout|econnreset|ehostunreach/i;

export interface ConnectFailureContext {
  /** `window.location.protocol`; an https page cannot call an http match server. */
  pageProtocol: string;
  /** False when the URL is the port-2567 guess (no `VITE_COLYSEUS_URL` in this build). */
  configured: boolean;
}

function failureMessage(error: unknown): string {
  if (error instanceof Error) return error.message.trim();
  if (typeof error === 'string') return error.trim();
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message.trim();
  }
  return '';
}

/** HTTP status or Colyseus code carried by the SDK's ServerError / MatchMakeError. */
function failureCode(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'number' && Number.isFinite(code) ? code : null;
}

/** One line a player can act on: which server, and why the join did not happen. */
export function describeConnectFailure(
  error: unknown,
  url: string,
  context: ConnectFailureContext,
): string {
  const message = failureMessage(error);
  const code = failureCode(error);
  if (code !== null) {
    const detail = message ? `${code}: ${message}` : String(code);
    return `The match server at ${url} rejected the join (${detail}).`;
  }
  if (!message || NETWORK_FAILURE.test(message)) {
    let text = `Could not reach the match server at ${url}.`;
    if (context.pageProtocol === 'https:' && /^http:/i.test(url)) {
      text +=
        ' Browsers block an http:// match server from an https:// page; serve it over https:// (wss).';
    } else if (!context.configured) {
      text += ' This build has no VITE_COLYSEUS_URL, so it guessed port 2567 on the site host.';
    }
    return text;
  }
  return `${message} (match server ${url})`;
}

function connectFailureContext(): ConnectFailureContext {
  return {
    pageProtocol: typeof window === 'undefined' ? '' : window.location.protocol,
    configured: configuredColyseusUrl().length > 0,
  };
}

function explainJoinFailure(error: unknown, url: string): Error {
  console.error('Orbomb match server join failed', url, error);
  return new Error(describeConnectFailure(error, url, connectFailureContext()), { cause: error });
}

let shared: { url: string; client: Client } | null = null;

function sharedClient(url: string): Client {
  if (!shared || shared.url !== url) shared = { url, client: new Client(url) };
  return shared.client;
}

function wrapRoom(room: {
  send(type: string | number, payload?: unknown): void;
  onMessage(type: string | number, callback: (payload: unknown) => void): void;
  onDrop(callback: () => void): void;
  onReconnect(callback: () => void): void;
  onLeave(callback: (code: number) => void): void;
  leave(consented?: boolean): Promise<number>;
  reconnection: {
    enabled: boolean;
    maxRetries: number;
    delay: number;
    minDelay: number;
    maxDelay: number;
  };
  reconnectionToken: string;
}): MatchSocket {
  configureReconnection(room.reconnection);
  return {
    send(type, payload) {
      room.send(type, payload);
    },
    onMessage(type, handler) {
      room.onMessage(type, handler);
    },
    onDrop(handler) {
      room.onDrop(() => handler());
    },
    onReconnect(handler) {
      room.onReconnect(() => handler());
    },
    onLeave(handler) {
      room.onLeave(() => handler());
    },
    disableReconnection() {
      stopReconnection(room.reconnection);
    },
    leave(consented = true) {
      stopReconnection(room.reconnection);
      void room.leave(consented);
    },
    get reconnectionToken() {
      return room.reconnectionToken;
    },
  };
}

export function colyseusConnect(url: string): ConnectMatch {
  return async (options) => {
    const client = sharedClient(url);
    if (options.accessToken) client.auth.token = options.accessToken;
    const room = await client.joinOrCreate(ROOM_NAME, options).catch((error: unknown) => {
      throw explainJoinFailure(error, url);
    });
    return wrapRoom(room);
  };
}

export function colyseusReconnect(url: string): ReconnectMatch {
  return async (reconnectionToken) => {
    const room = await sharedClient(url)
      .reconnect(reconnectionToken)
      .catch((error: unknown) => {
        throw explainJoinFailure(error, url);
      });
    return wrapRoom(room);
  };
}
